import { usePortalReturnCounts } from '../components/returns/returnsShared';
import { useState, type ReactNode, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Truck,
  Search,
  SlidersHorizontal,
  Info,
  User,
  ChevronDown,
  ShoppingCart,
  LogOut,
} from 'lucide-react';
import { useAuthStore } from '../stores/authStore';
import { useCapabilitiesStore } from '../stores/capabilitiesStore';
import { ThemeToggle } from '../components/ui/ThemeToggle';
import { useCartStore } from './cartStore';
import { MdsLogo } from '../components/MdsLogo';
import { NotificationBell } from '../components/NotificationBell';
import { VehicleDialog } from './VehicleDialog';
import i18n from '../i18n';
import { AppShell } from '../components/AppShell';

// Search scope ↔ backend catalogue search mode (CatalogService SEARCH_MODES).
const SCOPES = ['ALL', 'TEXT', 'EAN', 'SKU'] as const;

const SUPPORT_EMAIL = 'support@mecaradar.com';

interface Crumb {
  label: string;
  /** Present on ancestor crumbs (clickable); omitted on the current page. */
  to?: string;
}

/** Breadcrumb trail for the current customer route. Home (catalogue) is always the root;
 *  list pages add one crumb, detail/creation pages add their parent list + a leaf. */
function crumbsFor(pathname: string): Crumb[] {
  const HOME = '/b2b/catalog';
  const c = (key: string) => i18n.t(`portal.crumbs.${key}`);
  if (pathname === HOME || pathname === '/') return [{ label: c('home') }];

  const home: Crumb = { label: c('home'), to: HOME };
  if (pathname === '/orders' || pathname === '/b2b/orders') return [home, { label: c('orders') }];
  if (pathname === '/orders/new')
    return [home, { label: c('orders'), to: '/b2b/orders' }, { label: c('newOrder') }];
  if (pathname.startsWith('/orders/'))
    return [home, { label: c('orders'), to: '/b2b/orders' }, { label: c('order') }];
  if (pathname === '/quotes') return [home, { label: c('quotes') }];
  if (pathname.startsWith('/quotes/'))
    return [home, { label: c('quotes'), to: '/quotes' }, { label: c('quote') }];
  // Returns v2 — the garage area's returns (new · tracking · one return · credits).
  const returns: Crumb = { label: c('returns'), to: '/b2b/returns' };
  if (pathname === '/b2b/returns') return [home, { label: c('returns') }];
  if (pathname === '/b2b/returns/new') return [home, returns, { label: c('newReturn') }];
  if (pathname === '/b2b/returns/credits') return [home, returns, { label: c('credits') }];
  if (pathname.startsWith('/b2b/returns/')) return [home, returns, { label: c('return') }];
  if (pathname === '/profile') return [home, { label: c('profile') }];
  if (pathname === '/notifications') return [home, { label: c('notifications') }];
  return [home];
}

interface Props {
  children: ReactNode;
}

/**
 * Customer-facing portal shell — since feedback 3.4 (2026-10-22) the SAME layout as the admin:
 * the menu column on the left (AppShell, items from /me/capabilities, grouped and iconed like
 * the staff menu), with the portal's own appbar — vehicle, parts search, filters, notifications,
 * information, account and cart — and its breadcrumb above the page. It used to be a top bar
 * with the menu folded behind a hamburger.
 */
export function CustomerShell({ children }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user, logout } = useAuthStore();
  const caps = useCapabilitiesStore((s) => s.data);
  const clearCaps = useCapabilitiesStore((s) => s.clear);
  const cartCount = useCartStore((s) => s.lines.reduce((n, l) => n + l.qty, 0));

  const [q, setQ] = useState('');
  const [scope, setScope] = useState('ALL');
  const [profileOpen, setProfileOpen] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const [vehicleOpen, setVehicleOpen] = useState(false);

  const catalogUrl = (term: string): string => {
    const qs = new URLSearchParams();
    if (term.trim()) qs.set('q', term.trim());
    if (scope !== 'ALL') qs.set('mode', scope);
    const s = qs.toString();
    return `/b2b/catalog${s ? `?${s}` : ''}`;
  };

  const onSearch = (e: FormEvent) => {
    e.preventDefault();
    navigate(catalogUrl(q));
  };

  const signOut = () => {
    clearCaps();
    logout();
  };

  const companyName = user?.email ?? t('portal.myAccount');
  const navItems = caps?.navigation.items ?? [];
  const returnCounts = usePortalReturnCounts(navItems.some((i) => i.path === '/b2b/returns'));
  const crumbs = crumbsFor(pathname);

  const appbar = (
    <>
      {/* The logo is at the top of the menu column; on a phone the column is a drawer. */}
      <Link to="/b2b/catalog" style={{ textDecoration: 'none' }} className="md:hidden">
        <MdsLogo />
      </Link>

      <button
        className="nav icon-only"
        type="button"
        aria-label={t('portal.vehicleData')}
        title={t('portal.vehicleData')}
        onClick={() => setVehicleOpen(true)}
      >
        <Truck size={18} />
      </button>

      <form className="search" onSubmit={onSearch} role="search">
        <span className="ic">
          <Search size={17} />
        </span>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t('portal.searchPlaceholder')}
          aria-label={t('portal.searchAria')}
        />
        <span className="scope is-select">
          <select
            value={scope}
            onChange={(e) => setScope(e.target.value)}
            aria-label={t('portal.searchScope')}
          >
            {SCOPES.map((sc) => (
              <option key={sc} value={sc}>
                {t(`portal.scopes.${sc}`)}
              </option>
            ))}
          </select>
        </span>
        <button className="go" type="submit">
          <Search size={16} /> {t('common.search')}
        </button>
      </form>

      <button
        className="nav"
        type="button"
        aria-label={t('portal.filters')}
        title={t('portal.filtersTitle')}
        onClick={() => navigate(catalogUrl(q))}
      >
        <SlidersHorizontal size={16} />
      </button>

      <div className="right">
        {/* The shared bell: unread badge and store, same component as the staff appbar. */}
        <NotificationBell size={18} />

        <div className="menu-wrap">
          <button
            className="iconbutton"
            type="button"
            aria-label={t('portal.information')}
            aria-expanded={infoOpen}
            onClick={() => setInfoOpen((o) => !o)}
          >
            <Info size={18} />
          </button>
          {infoOpen && (
            <>
              <div className="menu-overlay" onClick={() => setInfoOpen(false)} />
              <div className="menu right" role="menu">
                <div className="mitem between" style={{ fontWeight: 700 }}>
                  {t('portal.portalName')}
                </div>
                <div className="sep" />
                <div className="info-note">{t('portal.infoNote')}</div>
                <div className="sep" />
                <a className="mitem" href={`mailto:${SUPPORT_EMAIL}`}>
                  <Info size={14} /> {t('portal.contactSupport')}
                </a>
              </div>
            </>
          )}
        </div>

        <div className="acct">
          <button
            className="iconbutton"
            aria-label={t('portal.myAccount')}
            onClick={() => setProfileOpen((o) => !o)}
          >
            <User size={18} />
          </button>
          <div className="who">
            <div className="n">{companyName}</div>
            <div className="s">{t(`roles.${user?.role}`, { defaultValue: user?.role ?? '' })}</div>
          </div>
        </div>

        <div className="menu-wrap">
          <button
            className="iconbutton"
            aria-label={t('portal.accountMenu')}
            aria-expanded={profileOpen}
            onClick={() => setProfileOpen((o) => !o)}
          >
            <ChevronDown size={16} />
          </button>
          {profileOpen && (
            <>
              <div className="menu-overlay" onClick={() => setProfileOpen(false)} />
              <div className="menu right" role="menu">
                <Link to="/profile" role="menuitem" onClick={() => setProfileOpen(false)}>
                  <User size={14} /> {t('portal.myProfile')}
                </Link>
                <div className="mitem between">
                  <span>{t('portal.theme')}</span>
                  <ThemeToggle className="h-8 w-8" />
                </div>
                <div className="sep" />
                <button className="mitem" role="menuitem" onClick={signOut}>
                  <LogOut size={14} /> {t('portal.signOut')}
                </button>
              </div>
            </>
          )}
        </div>

        <button
          className="iconbutton cartbtn"
          aria-label={t('portal.viewCart')}
          onClick={() => navigate('/b2b/catalog')}
        >
          <ShoppingCart size={18} />
          {cartCount > 0 && <span className="badge">{cartCount}</span>}
        </button>
      </div>
    </>
  );

  const breadcrumb = (
    <nav className="crumb" aria-label={t('portal.breadcrumb')}>
      {crumbs.map((c, i) => {
        const last = i === crumbs.length - 1;
        return (
          <span key={i} className="contents">
            {c.to && !last ? (
              <Link to={c.to}>{c.label}</Link>
            ) : (
              <span className="on" aria-current={last ? 'page' : undefined}>
                {c.label}
              </span>
            )}
            {!last && <span>›</span>}
          </span>
        );
      })}
    </nav>
  );

  return (
    <>
      <AppShell
        variant="portal"
        appbar={appbar}
        subbar={breadcrumb}
        // Immersive builders (3-column catalogue / order layout) fill the viewport.
        fullBleedPaths={['/b2b/catalog']}
        // Returns accepted whose part the garage still has to send back (prototype counter).
        badges={returnCounts.toSend > 0 ? { '/b2b/returns': returnCounts.toSend } : {}}
      >
        {children}
      </AppShell>
      <VehicleDialog open={vehicleOpen} onClose={() => setVehicleOpen(false)} />
    </>
  );
}
